import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Copy, Power, CheckCircle2, Search, Users, KeyRound, AlertTriangle, Pencil, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { saas, errMsg } from "@/lib/saasApi";

function fmtDate(iso) {
  try { return iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"; } catch { return "—"; }
}
function daysRemaining(iso) {
  try { return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000); } catch { return null; }
}


export default function AdminCustomers() {
  const [orgs, setOrgs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", planId: "starter", firstUserEmail: "", firstUserFirstName: "", firstUserLastName: "", country: "BE" });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);

  // Manage-users / reset-password state
  const [usersOrg, setUsersOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(null); // user pending reset confirmation
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { email, temporaryPassword } shown once

  // Rename organisation
  const [renameOrg, setRenameOrg] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  // Segment filter
  const [seg, setSeg] = useState("all");

  // Capacity (owner-only provider-commitment warning)
  const [capacity, setCapacity] = useState(null);

  // Demo account creation
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({ companyName: "", firstName: "", lastName: "", email: "", note: "", expiryDays: 30 });
  const [demoCreating, setDemoCreating] = useState(false);
  const [demoCreated, setDemoCreated] = useState(null);

  // Demo lifecycle actions
  const [extendOrg, setExtendOrg] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [extending, setExtending] = useState(false);
  const [revokeOrg, setRevokeOrg] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [noteOrg, setNoteOrg] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const openRename = (org) => { setRenameOrg(org); setRenameValue(org.name); };
  const doRename = async () => {
    if (!renameValue.trim()) return toast.error("Enter an organisation name.");
    setRenaming(true);
    try {
      await saas.adminOrgName(renameOrg.id, renameValue.trim());
      toast.success("Organisation name updated.");
      setRenameOrg(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRenaming(false); }
  };

  const openUsers = async (org) => {
    setUsersOrg(org);
    setUsers([]);
    setResetConfirm(null);
    setResetResult(null);
    setUsersLoading(true);
    try {
      const { data } = await saas.adminOrgUsers(org.id);
      setUsers(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUsersLoading(false); }
  };

  const closeUsers = () => { setUsersOrg(null); setUsers([]); setResetConfirm(null); setResetResult(null); };

  const copyText = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Copied.");
        return;
      }
      throw new Error("no clipboard");
    } catch {
      toast.message("Copy manually — clipboard access is blocked in this view.");
    }
  };

  const doReset = async () => {
    if (!resetConfirm) return;
    setResetting(true);
    try {
      const { data } = await saas.adminResetUserPassword(resetConfirm.id);
      setResetResult(data);
      setResetConfirm(null);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setResetting(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([saas.adminOrgs(), saas.adminPlans(), saas.adminCapacity()])
      .then(([o, p, c]) => { setOrgs(o.data); setPlans(p.data); setCapacity(c.data); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createDemo = async () => {
    const f = demoForm;
    if (!f.companyName.trim() || !f.firstName.trim() || !f.lastName.trim() || !f.email.trim()) return toast.error("Enter company, contact name and email.");
    setDemoCreating(true);
    try {
      const { data } = await saas.adminCreateDemo({
        companyName: f.companyName.trim(), firstName: f.firstName.trim(), lastName: f.lastName.trim(),
        email: f.email.trim(), note: f.note.trim() || undefined, expiryDays: Number(f.expiryDays) || undefined,
      });
      setDemoCreated(data);
      setDemoForm({ companyName: "", firstName: "", lastName: "", email: "", note: "", expiryDays: 30 });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDemoCreating(false); }
  };
  const closeDemo = () => { setDemoOpen(false); setDemoCreated(null); };

  const doExtend = async () => {
    if (!extendOrg) return;
    setExtending(true);
    try { await saas.adminExtendDemo(extendOrg.id, Number(extendDays) || 30); toast.success("Demo credit extended."); setExtendOrg(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setExtending(false); }
  };

  const doRevoke = async () => {
    if (!revokeOrg) return;
    setRevoking(true);
    try { await saas.adminRevokeDemo(revokeOrg.id); toast.success("Demo credit revoked."); setRevokeOrg(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setRevoking(false); }
  };

  const openNote = (org) => { setNoteOrg(org); setNoteValue(org.internalNote || ""); };
  const doNote = async () => {
    if (!noteOrg) return;
    setSavingNote(true);
    try { await saas.adminOrgNote(noteOrg.id, noteValue.trim() || null); toast.success("Note saved."); setNoteOrg(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSavingNote(false); }
  };

  const create = async () => {
    if (!form.name.trim() || !form.firstUserEmail.trim()) return toast.error("Enter a name and email.");
    setCreating(true);
    try {
      const { data } = await saas.adminCreateOrg(form);
      setCreated(data);
      setForm({ name: "", planId: "starter", firstUserEmail: "", firstUserFirstName: "", firstUserLastName: "", country: "BE" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCreating(false); }
  };

  const toggle = async (org) => {
    const next = org.status === "active" ? "inactive" : "active";
    try { await saas.adminOrgStatus(org.id, next); toast.success(next === "active" ? "Activated." : "Deactivated."); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const changePlan = async (org, planId) => {
    try { await saas.adminOrgPlan(org.id, planId); toast.success("Plan updated."); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const closeCreate = () => { setOpen(false); setCreated(null); };

  const SEGMENTS = [
    { id: "all", label: "All" },
    { id: "manual", label: "Manual account" },
    { id: "no_package", label: "No package" },
    { id: "pending", label: "Pending package" },
    { id: "active", label: "Active" },
    { id: "deactivated", label: "Deactivated" },
    { id: "demo", label: "Demo (unused)" },
    { id: "demo_used", label: "Demo used" },
    { id: "converted", label: "Converted" },
    { id: "demo_expired", label: "Demo expired/revoked" },
  ];
  const matchSeg = (o) => {
    switch (seg) {
      case "manual": return o.billingMethod === "manual";
      case "no_package": return !o.planId && !o.pendingPlanId && o.subscriptionStatus !== "active";
      case "pending": return !!o.pendingPlanId && o.subscriptionStatus !== "active";
      case "active": return o.status === "active";
      case "deactivated": return o.status === "inactive";
      case "demo": return o.accountSource === "demo" && o.demoCredit?.status === "unused";
      case "demo_used": return o.accountSource === "demo" && o.demoCredit?.status === "used";
      case "converted": return o.accountSource === "demo" && !!o.convertedToPaidAt;
      case "demo_expired": return o.accountSource === "demo" && ["expired", "revoked"].includes(o.demoCredit?.status);
      default: return true;
    }
  };
  const filtered = orgs.filter((o) => {
    const q = query.toLowerCase();
    const hay = `${o.name} ${o.primaryUserEmail || ""} ${o.primaryUserName || ""}`.toLowerCase();
    return hay.includes(q) && matchSeg(o);
  });

  const planName = (id) => plans.find((p) => p.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="admin-customers">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold text-re-charcoal">Customers</h1>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setDemoCreated(null); setDemoOpen(true); }} data-testid="create-demo-open" variant="outline" className="rounded-full border-re-red text-re-red hover:bg-re-red/5"><Gift className="mr-1.5 h-4 w-4" /> Create demo account</Button>
          <Button onClick={() => { setCreated(null); setOpen(true); }} data-testid="create-org-open" className="rounded-full bg-re-red text-white hover:bg-re-deepred"><Plus className="mr-1.5 h-4 w-4" /> New customer</Button>
        </div>
      </div>

      {capacity?.overCommitted && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="capacity-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm">
              <p className="font-semibold text-re-charcoal">Capacity warning</p>
              <p className="mt-0.5 text-re-muted">{capacity.warning}</p>
              <p className="mt-1 text-xs text-re-muted" data-testid="capacity-detail">Committed credits: {capacity.totalSpendableCustomerCredits} ({capacity.outstandingDemoCredits} demo + {capacity.outstandingPaidCredits} paid) · Estimated capacity: {capacity.estimatedProviderBalance}</p>
            </div>
          </div>
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-re-muted" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search customers" data-testid="org-search" className="border-re-line pl-9" />
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="org-filters">
        {SEGMENTS.map((s) => (
          <button key={s.id} onClick={() => setSeg(s.id)} data-testid={`seg-${s.id}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${seg === s.id ? "bg-re-charcoal text-white" : "border border-re-line bg-white text-re-muted hover:text-re-charcoal"}`}>{s.label}</button>
        ))}
      </div>

      {loading ? <p className="text-sm text-re-muted">Loading…</p> : (
        <div className="space-y-3" data-testid="org-list">
          {filtered.map((org) => {
            const isDemo = org.accountSource === "demo";
            const demoStatus = org.demoCredit?.status;
            const exp = org.demoCredit?.expiresAt;
            const converted = !!org.convertedToPaidAt;
            let daysLabel = "—";
            if (demoStatus === "unused") { const dr = daysRemaining(exp); daysLabel = dr == null ? "—" : dr < 0 ? "Expired" : dr; }
            else if (demoStatus === "expired") daysLabel = "Expired";
            else if (demoStatus === "revoked") daysLabel = "Revoked";
            return (
            <div key={org.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-re-line bg-white p-4" data-testid={`org-${org.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-medium text-re-charcoal" data-testid={`org-name-${org.id}`}>{org.name}</p>
                  <Badge variant={org.status === "active" ? "default" : "secondary"} className={org.status === "active" ? "bg-emerald-100 text-emerald-700" : ""}>{org.status === "active" ? "Active" : "Deactivated"}</Badge>
                  {isDemo && demoStatus === "unused" && <Badge className="bg-re-red/10 text-re-red" data-testid={`org-demo-badge-${org.id}`}>Demo</Badge>}
                  {converted && <Badge className="bg-emerald-100 text-emerald-700" data-testid={`org-converted-badge-${org.id}`}>Converted</Badge>}
                  <button onClick={() => openRename(org)} data-testid={`org-rename-${org.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-re-muted transition-colors hover:text-re-red"><Pencil className="h-3.5 w-3.5" /> Edit name</button>
                </div>
                <p className="mt-1 truncate text-xs text-re-muted">{org.primaryUserName ? `${org.primaryUserName} · ` : ""}{org.primaryUserEmail || "—"} · {org.availableCredits} credits · {org.billingMethod === "manual" ? "Manual" : "Self-service"}</p>
                <p className="mt-0.5 text-xs text-re-muted">
                  {org.subscriptionStatus === "active" && org.planId ? `Active plan: ${planName(org.planId)}` : org.pendingPlanId ? `Pending plan: ${planName(org.pendingPlanId)}` : org.planId ? `Plan: ${planName(org.planId)}` : "No package"}
                  {org.billingLabel ? <span className="ml-1 font-medium text-re-charcoal" data-testid={`org-billing-state-${org.id}`}>· {org.billingLabel}</span> : null}
                  {org.billing?.cancelScheduled && org.billing?.accessUntil ? <span className="text-amber-700"> (until {new Date(org.billing.accessUntil).toLocaleDateString("en-GB")})</span> : null}
                </p>
                {isDemo && (
                  <div className="mt-2 rounded-lg border border-re-red/20 bg-re-red/5 px-3 py-2 text-xs" data-testid={`org-demo-${org.id}`}>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      {converted ? (
                        <>
                          <span className="font-medium text-emerald-700" data-testid={`demo-converted-${org.id}`}>Converted to paid</span>
                          <span className="text-re-muted">Plan: <span className="font-medium text-re-charcoal">{planName(org.paidPlan)}</span></span>
                        </>
                      ) : demoStatus === "used" ? (
                        <span className="text-re-muted" data-testid={`demo-status-${org.id}`}>Demo credit: <span className="font-medium text-re-charcoal">Used</span></span>
                      ) : (
                        <>
                          <span className="text-re-muted" data-testid={`demo-status-${org.id}`}>Demo status: <span className="font-medium capitalize text-re-charcoal">{demoStatus || "—"}</span></span>
                          <span className="text-re-muted">Expires: <span className="font-medium text-re-charcoal">{fmtDate(exp)}</span></span>
                          <span className="text-re-muted" data-testid={`demo-days-${org.id}`}>Days remaining: <span className="font-medium text-re-charcoal">{daysLabel}</span></span>
                        </>
                      )}
                    </div>
                    {org.internalNote && <p className="mt-1 text-re-muted" data-testid={`demo-note-text-${org.id}`}>Note: {org.internalNote}</p>}
                  </div>
                )}
                {org.billing && (org.billing.stripeCustomerId || org.billing.stripeSubscriptionId) && (
                  <p className="mt-0.5 break-all font-mono text-[11px] text-re-muted" data-testid={`org-stripe-${org.id}`}>
                    Stripe: {org.billing.stripeStatus || org.subscriptionStatus || "—"} · cus {org.billing.stripeCustomerId || "—"} · sub {org.billing.stripeSubscriptionId || "—"}
                    {org.billing.cancelScheduled ? " · cancel scheduled" : ""}
                    {org.billing.accessUntil ? ` · access until ${new Date(org.billing.accessUntil).toLocaleDateString("en-GB")}` : (org.billing.currentPeriodEnd ? ` · renews ${new Date(org.billing.currentPeriodEnd).toLocaleDateString("en-GB")}` : "")}
                    {org.billing.lastInvoiceId ? ` · inv ${org.billing.lastInvoiceId}` : ""}
                    {org.billing.lastGrantAt ? ` · last grant ${new Date(org.billing.lastGrantAt).toLocaleDateString("en-GB")} (+${org.billing.lastGrantAmount})` : ""}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isDemo && demoStatus === "unused" && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setExtendOrg(org); setExtendDays(30); }} data-testid={`demo-extend-${org.id}`} className="rounded-full">Extend</Button>
                    <Button variant="outline" size="sm" onClick={() => setRevokeOrg(org)} data-testid={`demo-revoke-${org.id}`} className="rounded-full text-re-red hover:text-re-deepred">Revoke</Button>
                  </>
                )}
                {isDemo && (
                  <Button variant="outline" size="sm" onClick={() => openNote(org)} data-testid={`demo-note-${org.id}`} className="rounded-full"><Pencil className="mr-1 h-3.5 w-3.5" /> Note</Button>
                )}
                <Select value={org.planId || undefined} onValueChange={(v) => changePlan(org, v)}>
                  <SelectTrigger className="h-9 w-32 border-re-line" data-testid={`org-plan-${org.id}`}><SelectValue placeholder="Set plan" /></SelectTrigger>
                  <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.monthlyCredits})</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => openUsers(org)} data-testid={`org-users-${org.id}`} className="rounded-full"><Users className="mr-1 h-4 w-4" /> Users</Button>
                <Button variant="outline" size="sm" onClick={() => toggle(org)} data-testid={`org-toggle-${org.id}`} className="rounded-full"><Power className="mr-1 h-4 w-4" /> {org.status === "active" ? "Deactivate" : "Activate"}</Button>
              </div>
            </div>
            );
          })}
          {filtered.length === 0 && <p className="text-sm text-re-muted">No customers found.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeCreate())}>
        <DialogContent data-testid="create-org-modal">
          {!created ? (
            <>
              <DialogHeader><DialogTitle>Create new customer</DialogTitle><DialogDescription>An organisation with an administrator is created. Plan credits are granted immediately.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Agency name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="co-name" className="border-re-line" /></div>
                <div className="space-y-1.5"><Label>Administrator email</Label><Input type="email" value={form.firstUserEmail} onChange={(e) => setForm({ ...form, firstUserEmail: e.target.value })} data-testid="co-email" className="border-re-line" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>First name</Label><Input value={form.firstUserFirstName} onChange={(e) => setForm({ ...form, firstUserFirstName: e.target.value })} data-testid="co-first" className="border-re-line" /></div>
                  <div className="space-y-1.5"><Label>Last name</Label><Input value={form.firstUserLastName} onChange={(e) => setForm({ ...form, firstUserLastName: e.target.value })} data-testid="co-last" className="border-re-line" /></div>
                </div>
                <div className="space-y-1.5"><Label>Plan</Label>
                  <Select value={form.planId} onValueChange={(v) => setForm({ ...form, planId: v })}><SelectTrigger className="border-re-line" data-testid="co-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.monthlyCredits} credits</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeCreate} className="rounded-full">Cancel</Button>
                <Button onClick={create} disabled={creating} data-testid="co-submit" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{creating ? "Creating…" : "Create"}</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Customer created</DialogTitle><DialogDescription>Share these details securely. The temporary password is shown only once.</DialogDescription></DialogHeader>
              <div className="space-y-2 rounded-xl border border-re-line bg-re-soft p-4" data-testid="created-credentials">
                <div className="flex items-center justify-between"><span className="text-sm text-re-muted">Email</span><span className="text-sm font-medium text-re-charcoal">{created.email}</span></div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-re-muted">Temporary password</span>
                  <span className="flex items-center gap-1.5">
                    <code className="rounded bg-white px-2 py-1 text-sm font-semibold text-re-charcoal" data-testid="created-temp-password">{created.temporaryPassword}</code>
                    <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="copy-temp-password" onClick={() => copyText(created.temporaryPassword)}><Copy className="h-3.5 w-3.5" /></Button>
                  </span>
                </div>
                <p className="pt-1 text-xs text-re-muted">The user must set their own password on first sign-in.</p>
              </div>
              <DialogFooter><Button onClick={closeCreate} className="rounded-full bg-re-charcoal text-white" data-testid="created-done">Done</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage users + owner-only password reset */}
      <Dialog open={!!usersOrg} onOpenChange={(v) => { if (!v) closeUsers(); }}>
        <DialogContent data-testid="org-users-modal">
          {resetResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Password reset</DialogTitle>
                <DialogDescription>Share this temporary password securely. It is shown only once and is not stored anywhere.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 rounded-xl border border-re-line bg-re-soft p-4" data-testid="reset-credentials">
                <div className="flex items-center justify-between"><span className="text-sm text-re-muted">Email</span><span className="text-sm font-medium text-re-charcoal">{resetResult.email}</span></div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-re-muted">Temporary password</span>
                  <span className="flex items-center gap-1.5">
                    <code className="rounded bg-white px-2 py-1 text-sm font-semibold text-re-charcoal" data-testid="reset-temp-password">{resetResult.temporaryPassword}</code>
                    <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="copy-reset-password" onClick={() => copyText(resetResult.temporaryPassword)}><Copy className="h-3.5 w-3.5" /></Button>
                  </span>
                </div>
                <p className="pt-1 text-xs text-re-muted">All existing sessions for this user were signed out. They must set a new password on their next sign-in.</p>
              </div>
              <DialogFooter><Button onClick={closeUsers} className="rounded-full bg-re-charcoal text-white" data-testid="reset-done">Done</Button></DialogFooter>
            </>
          ) : resetConfirm ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-re-red" /> Reset password?</DialogTitle>
                <DialogDescription>
                  This generates a new one-time temporary password for <span className="font-medium text-re-charcoal">{resetConfirm.email}</span>, signs out all their sessions, and forces a password change on next sign-in. The organisation, credits, projects and videos are not affected.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setResetConfirm(null)} className="rounded-full" data-testid="reset-cancel">Cancel</Button>
                <Button onClick={doReset} disabled={resetting} data-testid="reset-confirm" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{resetting ? "Resetting…" : "Reset password"}</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Users — {usersOrg?.name}</DialogTitle>
                <DialogDescription>Manage the customer users in this organisation.</DialogDescription>
              </DialogHeader>
              {usersLoading ? <p className="text-sm text-re-muted">Loading…</p> : (
                <div className="space-y-2" data-testid="org-users-list">
                  {users.map((u) => (
                    <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-re-line bg-white p-3" data-testid={`user-${u.id}`}>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-re-charcoal">{[u.firstName, u.lastName].filter(Boolean).join(" ") || u.email}</p>
                        <p className="truncate text-xs text-re-muted">{u.email} · {u.role === "AGENCY_ADMIN" ? "Administrator" : "User"}{u.mustChangePassword ? " · must change password" : ""}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setResetConfirm(u)} data-testid={`reset-password-${u.id}`} className="rounded-full">
                        <KeyRound className="mr-1 h-4 w-4" /> Reset password
                      </Button>
                    </div>
                  ))}
                  {users.length === 0 && <p className="text-sm text-re-muted">No users found.</p>}
                </div>
              )}
              <DialogFooter><Button variant="outline" onClick={closeUsers} className="rounded-full" data-testid="users-close">Close</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit organisation display name (name only — nothing else changes) */}
      <Dialog open={!!renameOrg} onOpenChange={(v) => { if (!v) setRenameOrg(null); }}>
        <DialogContent data-testid="rename-org-modal">
          <DialogHeader>
            <DialogTitle>Edit organisation name</DialogTitle>
            <DialogDescription>Changes only the display name. Users, login, credits, projects, videos, clips and billing are not affected.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Organisation name</Label>
            <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} data-testid="rename-input" className="border-re-line" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOrg(null)} className="rounded-full" data-testid="rename-cancel">Cancel</Button>
            <Button onClick={doRename} disabled={renaming} data-testid="rename-save" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{renaming ? "Saving…" : "Save name"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create demo account + one-time credentials */}
      <Dialog open={demoOpen} onOpenChange={(v) => (v ? setDemoOpen(true) : closeDemo())}>
        <DialogContent data-testid="create-demo-modal">
          {!demoCreated ? (
            <>
              <DialogHeader>
                <DialogTitle>Create demo account</DialogTitle>
                <DialogDescription>Creates one prospect account with 1 complimentary project credit. If they subscribe later, this stays their permanent account — no second account is ever created.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Company name</Label><Input value={demoForm.companyName} onChange={(e) => setDemoForm({ ...demoForm, companyName: e.target.value })} data-testid="demo-company" className="border-re-line" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>First name</Label><Input value={demoForm.firstName} onChange={(e) => setDemoForm({ ...demoForm, firstName: e.target.value })} data-testid="demo-first" className="border-re-line" /></div>
                  <div className="space-y-1.5"><Label>Last name</Label><Input value={demoForm.lastName} onChange={(e) => setDemoForm({ ...demoForm, lastName: e.target.value })} data-testid="demo-last" className="border-re-line" /></div>
                </div>
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={demoForm.email} onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })} data-testid="demo-email" className="border-re-line" /></div>
                <div className="space-y-1.5"><Label>Expiry (days)</Label><Input type="number" min={1} max={365} value={demoForm.expiryDays} onChange={(e) => setDemoForm({ ...demoForm, expiryDays: e.target.value })} data-testid="demo-expiry" className="border-re-line" /><p className="text-xs text-re-muted">Defaults to 30 days. Only affects the complimentary credit.</p></div>
                <div className="space-y-1.5"><Label>Internal note (optional)</Label><Input value={demoForm.note} onChange={(e) => setDemoForm({ ...demoForm, note: e.target.value })} data-testid="demo-note-input" className="border-re-line" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeDemo} className="rounded-full">Cancel</Button>
                <Button onClick={createDemo} disabled={demoCreating} data-testid="demo-submit" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{demoCreating ? "Creating…" : "Create demo account"}</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Demo account created</DialogTitle>
                <DialogDescription>Share these details securely. The temporary password is shown only once and is never stored.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 rounded-xl border border-re-line bg-re-soft p-4" data-testid="demo-credentials">
                <div className="flex items-center justify-between"><span className="text-sm text-re-muted">Company</span><span className="text-sm font-medium text-re-charcoal">{demoCreated.companyName}</span></div>
                <div className="flex items-center justify-between"><span className="text-sm text-re-muted">Email</span><span className="text-sm font-medium text-re-charcoal">{demoCreated.email}</span></div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-re-muted">Temporary password</span>
                  <span className="flex items-center gap-1.5">
                    <code className="rounded bg-white px-2 py-1 text-sm font-semibold text-re-charcoal" data-testid="demo-temp-password">{demoCreated.temporaryPassword}</code>
                    <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="demo-copy-password-icon" onClick={() => copyText(demoCreated.temporaryPassword)}><Copy className="h-3.5 w-3.5" /></Button>
                  </span>
                </div>
                <div className="flex items-center justify-between"><span className="text-sm text-re-muted">Complimentary credits</span><span className="text-sm font-medium text-re-charcoal">{demoCreated.availableDemoCredits}</span></div>
                <div className="flex items-center justify-between"><span className="text-sm text-re-muted">Expires</span><span className="text-sm font-medium text-re-charcoal">{fmtDate(demoCreated.expiresAt)}</span></div>
                <p className="pt-1 text-xs text-re-muted">On first sign-in the user is required to set their own password.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => copyText(demoCreated.temporaryPassword)} data-testid="demo-copy-password" className="rounded-full"><Copy className="mr-1.5 h-4 w-4" /> Copy password</Button>
                <Button variant="outline" onClick={() => copyText(demoCreated.copyText)} data-testid="demo-copy-invite" className="rounded-full"><Copy className="mr-1.5 h-4 w-4" /> Copy full invite</Button>
              </div>
              <DialogFooter><Button onClick={closeDemo} className="rounded-full bg-re-charcoal text-white" data-testid="demo-done">Done</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Extend demo credit */}
      <Dialog open={!!extendOrg} onOpenChange={(v) => { if (!v) setExtendOrg(null); }}>
        <DialogContent data-testid="extend-demo-modal">
          <DialogHeader>
            <DialogTitle>Extend demo credit</DialogTitle>
            <DialogDescription>Set a new expiry for {extendOrg?.name}&apos;s complimentary credit, counted from today. Paid credits are never affected.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5"><Label>Expiry (days from today)</Label><Input type="number" min={1} max={365} value={extendDays} onChange={(e) => setExtendDays(e.target.value)} data-testid="extend-days" className="border-re-line" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendOrg(null)} className="rounded-full" data-testid="extend-cancel">Cancel</Button>
            <Button onClick={doExtend} disabled={extending} data-testid="extend-confirm" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{extending ? "Saving…" : "Extend"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke demo credit */}
      <Dialog open={!!revokeOrg} onOpenChange={(v) => { if (!v) setRevokeOrg(null); }}>
        <DialogContent data-testid="revoke-demo-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-re-red" /> Revoke demo credit?</DialogTitle>
            <DialogDescription>This removes the unused complimentary credit from {revokeOrg?.name}. Their account, login and history stay intact, and any paid credits are not affected.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeOrg(null)} className="rounded-full" data-testid="revoke-cancel">Cancel</Button>
            <Button onClick={doRevoke} disabled={revoking} data-testid="revoke-confirm" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{revoking ? "Revoking…" : "Revoke credit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Internal note */}
      <Dialog open={!!noteOrg} onOpenChange={(v) => { if (!v) setNoteOrg(null); }}>
        <DialogContent data-testid="note-modal">
          <DialogHeader>
            <DialogTitle>Internal note</DialogTitle>
            <DialogDescription>Private owner note for {noteOrg?.name}. Never shown to the customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5"><Label>Note</Label><Input value={noteValue} onChange={(e) => setNoteValue(e.target.value)} data-testid="note-input" className="border-re-line" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOrg(null)} className="rounded-full" data-testid="note-cancel">Cancel</Button>
            <Button onClick={doNote} disabled={savingNote} data-testid="note-save" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{savingNote ? "Saving…" : "Save note"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
